import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './confirm-dialog.css';

/**
 * Themed confirmation modal — replaces `window.confirm()` for destructive
 * actions. Matches the book-details / premium-gate look (radial gold dark
 * card in dark mode, cream card in light) so confirmations feel like part
 * of the site instead of a browser chrome popup.
 *
 * Props:
 *   open           — whether to render
 *   title          — short headline (no period)
 *   body           — explanation; highlight in light/dark variants
 *   confirmLabel   — button text (e.g. "Delete profile")
 *   cancelLabel    — defaults to "Cancel"
 *   tone           — "danger" | "neutral" (default "danger")
 *   busy           — disables buttons + shows progress on confirm
 *   onConfirm()    — required
 *   onClose()      — required (Esc / Cancel / backdrop)
 */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  busy = false,
  confirmDisabled = false,
  cardClassName = '',
  children,
  onConfirm,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onConfirm, onClose, busy]);

  if (!open) return null;

  return createPortal(
    <div
      className="confirm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className={`confirm-card confirm-card--${tone} ${cardClassName}`}>
        <div className={`confirm-icon confirm-icon--${tone}`} aria-hidden="true">
          {tone === 'danger' ? (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9.5" />
              <line x1="12" y1="11" x2="12" y2="16.5" />
              <circle cx="12" cy="7.5" r="0.4" fill="currentColor" />
            </svg>
          )}
        </div>

        <h2 id="confirm-title" className="confirm-title">{title}</h2>
        {body && <p className="confirm-body">{body}</p>}

        {children}
        <div className="confirm-actions">
          <button
            type="button"
            className="confirm-btn confirm-btn-ghost"
            onClick={onClose}
            disabled={busy}
            autoFocus
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-btn confirm-btn-${tone}`}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
